import { AnimatingLogo } from '@eazydraft/ui';
import { Info, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type NavigateFunction, useNavigate } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  AccessDeniedError,
  clearSignedOutElsewhere,
  DeploymentClaimError,
  exchangeBootstrap,
  SeatTakenError,
  wasSignedOutElsewhere,
} from '@/config/auth/deviceToken';
import { DialogBlockedError } from '@/config/auth/officeDialogManager';
import { type AuthDialogResult, useAuthDialog } from '@/features/auth/hooks/useAuthDialog';
import { ROUTES } from '@/utils/routeUtils';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';
import { useThemeStore } from '@/utils/stores/useThemeStore';

/** The user-facing message for a failed login surface. */
function loginErrorMessage(error: Error, t: (key: string) => string): string {
  if (error instanceof DialogBlockedError) {
    // Outlook's "allow dialog" prompt was ignored, or the browser blocks
    // pop-ups — fixed by the user, so tell them what to change.
    return t('login.error.dialogBlocked');
  }
  return error.message || t('login.error.default');
}

/**
 * Exchanges the bootstrap JWT for a device session and enters the app. The
 * mailbox-connection gate in ProtectedRoute decides whether the user lands on
 * Connect Mailbox or straight in the app.
 */
async function exchangeAndEnter(
  token: string,
  navigate: NavigateFunction,
  setErrorMessage: (message: string) => void,
  t: (key: string) => string,
): Promise<void> {
  try {
    await exchangeBootstrap(token);
    navigate(ROUTES.HOME, { replace: true });
  } catch (error) {
    if (error instanceof AccessDeniedError) {
      navigate(`${ROUTES.FORBIDDEN}?reason=${error.reason ?? ''}`, { replace: true });
      return;
    }
    if (error instanceof DeploymentClaimError) {
      setErrorMessage(error.message);
      return;
    }
    if (error instanceof SeatTakenError) {
      setErrorMessage(t('login.seat.takenMeanwhile'));
      return;
    }
    setErrorMessage(error instanceof Error ? error.message : t('login.error.default'));
  }
}

/**
 * Acts on a login that came home through the handoff claim (the window handle
 * was severed), landing where the fast path's exchange would.
 */
function enterFromClaim(
  result: Exclude<AuthDialogResult, { kind: 'bootstrap' } | { kind: 'cancelled' }>,
  navigate: NavigateFunction,
  setErrorMessage: (message: string) => void,
  t: (key: string) => string,
): void {
  if (result.kind === 'denied') {
    // The login completed but org access was refused. Route to the
    // explanation exactly as the fast path's 403 does.
    navigate(`${ROUTES.FORBIDDEN}?reason=${result.reason ?? ''}`, { replace: true });
    return;
  }
  if (result.kind === 'seat_taken') {
    setErrorMessage(t('login.seat.takenMeanwhile'));
    return;
  }
  // A device session is already stored, so there is no bootstrap left to
  // exchange.
  navigate(ROUTES.HOME, { replace: true });
}

/** Explains a sign-out caused by the account signing in to Outlook elsewhere. */
function SignedOutElsewhereNotice() {
  const { t } = useTranslation();
  // Read in the initializer, cleared in an effect: StrictMode runs the
  // initializer twice, and clearing there would hide the note on the second run.
  const [signedOutElsewhere] = useState(wasSignedOutElsewhere);
  useEffect(() => {
    clearSignedOutElsewhere();
  }, []);

  if (!signedOutElsewhere) {
    return null;
  }
  return (
    <Alert className="mb-6 w-full" data-testid="signed-out-elsewhere-alert">
      <Info className="h-4 w-4" />
      <AlertDescription>{t('login.seat.signedOutElsewhere')}</AlertDescription>
    </Alert>
  );
}

function LoginHeading() {
  const { t } = useTranslation();
  return (
    <>
      <div className="mb-8 text-center">
        <h1 className="text-primary mb-1 text-lg font-semibold">{t('login.title')}</h1>
        <p className="text-tertiary text-xs">{t('login.subtitle')}</p>
      </div>
      <SignedOutElsewhereNotice />
    </>
  );
}

export const Login = () => {
  const navigate = useNavigate();
  const { authError, setAuthError } = useAuthStore();
  // Asset swap instead of icon-themed: AnimatingLogo's entrance keyframes
  // animate `filter`, which would suppress the invert during the spin.
  const theme = useThemeStore((state) => state.theme);
  const [errorMessage, setErrorMessage] = useState<string | null>(authError);
  const [isExchanging, setIsExchanging] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (authError) {
      // Clear the error from store immediately so it doesn't persist on page reload loops
      // We rely on local state 'errorMessage' to show it for a few seconds
      setAuthError(null);
    }
  }, [authError, setAuthError]);

  // Opens the Clerk login surface (an Office Dialog in Office hosts; a popup
  // only in dev/non-Office contexts) and returns either a bootstrap JWT to
  // exchange for a device session, or an already-stored device session when
  // the login came home through the handoff claim.
  const authDialog = useAuthDialog();

  const handleSignIn = () => {
    setErrorMessage(null);
    authDialog.mutate(undefined, {
      onError: (error) => {
        setErrorMessage(loginErrorMessage(error, t));
      },
      onSuccess: async (result) => {
        if (result.kind === 'cancelled') {
          return; // stay on the login page silently
        }
        if (result.kind !== 'bootstrap') {
          enterFromClaim(result, navigate, setErrorMessage, t);
          return;
        }
        setIsExchanging(true);
        try {
          await exchangeAndEnter(result.token, navigate, setErrorMessage, t);
        } finally {
          setIsExchanging(false);
        }
      },
    });
  };

  const { isInitialized } = useOfficeContextStore();
  const isLoading = authDialog.isPending || isExchanging || !isInitialized;

  return (
    <div className="h-viewport bg-background flex flex-col items-center justify-center px-2">
      {/* Logo */}
      <AnimatingLogo
        alt="Lucio AI Logo"
        className="mb-4 h-16 w-auto"
        src={theme === 'dark' ? '/assets/lucioSymbolLight.svg' : '/assets/lucioSymbol.svg'}
      />

      <div className="w-full max-w-sm">
        <LoginHeading />

        <div className="space-y-6">
          <Button
            className="border-border-strong text-secondary hover:bg-back-bg2 bg-card flex w-full items-center justify-center gap-2 py-6 font-medium"
            disabled={isLoading}
            loading={isLoading}
            onClick={handleSignIn}
            variant="outline"
          >
            {!isInitialized
              ? t('login.initializing')
              : isLoading
                ? t('login.signingIn')
                : t('login.signIn')}
          </Button>
        </div>

        {/* Error Message Display */}
        {errorMessage && (
          <Alert className="mt-6 w-full" data-testid="error-alert" variant="destructive">
            <TriangleAlert className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
